import GameSearch from '@/components/game-reviews/GameSearch';
import { GetStaticProps, NextPage } from 'next';
import { GameReview, retrieveAllGameReviews } from '@/utils/api/prismic/prismicClient';
import Image from 'next/image';
import React, { ReactElement, useMemo, useState } from 'react';
import { PrismicRichText } from '@prismicio/react';
import classNames from 'classnames';
import RiCloseFill from '~icons/ri/close-fill'
import ReviewCardBlock from '@/components/game-reviews/ReviewCardBlock';
import ReviewCardFlex from '@/components/game-reviews/ReviewCardFlex';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { embedSerializer } from '@/utils/reviewEmbedSerializer';
import { HeadMetadata } from '@/components/HeadMetadata';
import { datetimeInLocaleLanguageAndTimezone, timeAgo } from '@/utils/timeFormatters';
import styles from '@/components/game-reviews/game-reviews.module.css'
import { NextPageWithLayout } from './_app';
import RootLayout from './RootLayout';

interface Props {
  gameReviews: GameReview[];
  siteUrl: string;
}

enum ReviewScore {
  Any = 'All',
  Masterpiece = 'Шедевр',
  Good = 'Советую',
  Beer = 'Под пиво',
  Badly = 'Так себе',
  Terrible = 'Ужасно'
}

const GameReviews: NextPageWithLayout<Props> = (props) => {
  const [selectedReview, setSelectedReview] = useState<GameReview>();
  const [searchScore, setSearchScore] = useState<ReviewScore>(ReviewScore.Any);
  const [searchQ, setSearchQ] = useState<string>('');
  const [searchGenres, setSearchGenres] = useState<string[]>([]);
  const [displayType, setDisplayType] = useState('block');
  const [isAutoScrollEnabled, setAutoScroll] = useState<boolean>(false);
  const router = useRouter();

  const serializer = embedSerializer;
  
  const gameReviews = useMemo(() => {
    if (!searchQ.trim() && searchScore === ReviewScore.Any && searchGenres.length === 0) {
      return props.gameReviews;
    }

    return props.gameReviews
      .filter((gameReview) => {
        return searchScore === ReviewScore.Any || searchScore === gameReview.score.label;
      })
      .filter((gameReview) => {
        const query = searchQ.trim().toLowerCase();
        const title = gameReview.title.toLowerCase();
        return query.length === 0 || title.includes(query);
      })
      .filter((gameReview) => {
        return searchGenres.length === 0 || searchGenres.some((genre) => gameReview.genres.includes(genre));
      });
  }, [searchQ, searchScore, searchGenres, props.gameReviews]);

  // Если всё-таки делать контейнеры с скроллбарами
  // style={{height: 'calc(100vh - 26vh)', scrollbarWidth: 'thin'}}

  return (
    <>
      <HeadMetadata
        pageTitle={selectedReview !== undefined
          ? `${selectedReview.title} › Игровые рецензии › Тиджой`
          : 'Игровые рецензии › Тиджой'}
        pageDescription="Обзоры на игры, ссылки на видео и плейлисты от Тиджоя."
        canonicalUrl={`${props.siteUrl}/game-reviews`}
        robots="index,follow"
      />

      <div className='relative'>
      <GameSearch
        onSearchScoreChanges={setSearchScore}
        onSearchQChanges={setSearchQ}
        onDisplayTypeChanges={setDisplayType}
        onGenresChanges={setSearchGenres}
        onAutoScrollChanges={setAutoScroll}/>
        
      <section className={classNames('grid gap-5 my-5', {'grid-cols-1': selectedReview === undefined, 'md:grid-cols-2': selectedReview !== undefined})}>
        {gameReviews.length === 0
          ? <article className='max-w-4xl mx-auto flex justify-center'>
            <p className='flex flex-row gap-3 items-center bg-[#151515]/50 rounded-2xl w-fit h-fit px-5 my-5 p-3 text-sm border border-white/10'>
              Не найдено ни одной рецензии
            </p>
          </article>
          : <section
            className={classNames('grid gap-5 overflow-hidden h-fit',
              {'grid-cols-1 md:grid-cols-1': selectedReview !== undefined && displayType === 'flex', 'grid-cols-1 md:grid-cols-2': selectedReview === undefined && displayType === 'flex',
                'grid-cols-2 md:grid-cols-3': selectedReview !== undefined && displayType === 'block', 'grid-cols-2 md:grid-cols-6': selectedReview === undefined && displayType === 'block'})}>
            {gameReviews.map((game, index) =>
              displayType == 'block'
                ? <ReviewCardBlock key={index} onReviewSelected={setSelectedReview} game={game} isAutoScroll={isAutoScrollEnabled}/>
                : <ReviewCardFlex key={index} onReviewSelected={setSelectedReview} game={game} isAutoScroll={isAutoScrollEnabled}/>
            )}
          </section>
        }

        {selectedReview === undefined
          ? ''
          : <article 
              className={`${styles['container'] as string} w-fit md:top-28 md:sticky h-auto md:h-[43rem] flex flex-col gap-5 rounded-xl md:overflow-y-auto order-first md:order-none md:pr-5`}>
              <div className='relative'>
                <div className={'absolute flex flex-row items-center justify-end gap-3 order-first md:order-none w-full md:w-fit text-center right-0'}>
                  <button onClick={() => {
                      setSelectedReview(undefined);
                      router.push('/game-reviews', undefined, {
                        shallow: true,
                      });
                    }} 
                    className='bg-[#0B0B0B] hover:bg-rose-700 p-2 rounded-lg transition-colors duration-200 ease-in-out m-2'>
                    <RiCloseFill/>
                  </button>
                </div>
                <Image
                  src={selectedReview!.cover!.sizes!.preview.url}
                  alt={selectedReview!.cover!.alt}
                  width={640}
                  height={320}
                  
                  className='w-full rounded-xl'/>
              </div>

              <section className='flex flex-row flex-wrap gap-3 items-center'>
                <p className={'bg-[#0B0B0B] px-3 py-1 rounded-xl text-base'}>{selectedReview.score.label} {selectedReview.score.emoji}</p>
                <h1 className={'text-2xl font-bold'}>{selectedReview.title}</h1>
              </section>

              <section className='flex flex-col gap-1'>
                <p className='text-zinc-500'>Описание</p>
                <section className='flex flex-col gap-5'>
                  <PrismicRichText
                    field={selectedReview.review}
                    components={serializer}/>
                </section>
              </section>

              <section className='flex flex-row flex-wrap gap-2 justify-start'>
                <div className='inline-flex flex-row md:flex-row flex-wrap gap-2 w-full'>
                  <p className='bg-[#0B0B0B] py-3 px-5 rounded-xl text-center grow'>
                      <span className='text-zinc-500 mr-1'>Дата создания</span> {datetimeInLocaleLanguageAndTimezone(new Date(selectedReview.createdAt))}
                  </p>
                  {selectedReview.genres.map((genre, index) =>
                    <p key={index} className={classNames('bg-[#0B0B0B] py-3 px-5 rounded-xl text-center grow md:grow-0', {'col-span-2': genre.length >= 12})}>{genre}</p>
                  )}
                </div>
              </section>

              <div className='block md:hidden my-5 mx-auto rounded-full w-64 h-0.5 bg-[#0B0B0B]'></div>
            </article>
          }
        </section>
      </div>
    </>
  )
}

GameReviews.getLayout = function getLayout(page: ReactElement) {
  return (
      <RootLayout>{page}</RootLayout>
  )
}

export default GameReviews;

export const getStaticProps: GetStaticProps<Props> = async () => {
  const [gameReviews] = await Promise.all([
    retrieveAllGameReviews(process.env['PRISMIC_REPOSITORY'] as string, process.env['PRISMIC_ACCESS_TOKEN'] as string),
  ] as const);

  return {
    props: {
      gameReviews,
      siteUrl: process.env['SITE_URL'] as string,
    },
  };
};